import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import apolloService from "../../../utils/apolloService";
import { supabase } from "../../../utils/supabase";

export default function ApolloCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState("Processing authorization...");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const error = searchParams.get("error");

        if (error) {
          setStatus("error");
          setMessage(`Authorization failed: ${error}`);
          setTimeout(() => navigate("/campaigns"), 3000);
          return;
        }

        if (!code) {
          setStatus("error");
          setMessage("No authorization code received");
          setTimeout(() => navigate("/campaigns"), 3000);
          return;
        }

        // Get current user ID
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user) {
          setStatus("error");
          setMessage("No active session. Please log in.");
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        // Exchange code for token
        const result = await apolloService.handleAuthCallback(code, state, user.id);

        if (result.success) {
          setStatus("success");
          setMessage("Apollo account connected successfully!");
          // Redirect to campaigns page after 2 seconds
          setTimeout(() => navigate("/campaigns"), 2000);
        } else {
          setStatus("error");
          // If the service returned rawText (HTML/404), show a trimmed preview to help debugging
          if (result.rawText) {
            const preview = result.rawText.length > 500 ? result.rawText.slice(0, 500) + "..." : result.rawText;
            setMessage(`${result.error || "Failed to authorize Apollo account"}\n\nResponse preview:\n${preview}`);
          } else {
            setMessage(result.error || "Failed to authorize Apollo account");
          }
          setTimeout(() => navigate("/campaigns"), 3000);
        }
      } catch (error) {
        console.error("Callback error:", error);
        setStatus("error");
        setMessage("An unexpected error occurred");
        setTimeout(() => navigate("/campaigns"), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {status === "processing" && (
            <>
              <div className="w-12 h-12 rounded-full border-4 border-blue-400/30 border-t-blue-400 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Processing Authorization</h2>
              <p className="text-slate-300">{message}</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-12 h-12 rounded-full bg-green-500/20 border border-green-500 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Success!</h2>
              <p className="text-green-300">{message}</p>
              <p className="text-slate-400 text-sm mt-4">Redirecting to campaigns...</p>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-12 h-12 rounded-full bg-red-500/20 border border-red-500 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Authorization Failed</h2>
              <p className="text-red-300">{message}</p>
              <p className="text-slate-400 text-sm mt-4">Redirecting to campaigns...</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
